import React from "react";
import { Link } from "react-router-dom";

import JobApplicationTabEmptyState, {
  JobApplicationTabEmptyStateStatus,
} from "@ats/src/views/jobApplications/Plato/JobApplicationTabEmptyState";

interface PlatoTabEmptyStateProps {
  status: JobApplicationTabEmptyStateStatus;
  creditsRemaining?: number;
  isAdmin?: boolean;
  onClick?: () => void;
  failedDueToNoJobCriteria?: boolean;
  jobId?: number;
}

interface PlatoEmptyStateConfig {
  icon: string;
  title: string;
  message: string;
  buttonLabel: string | null;
  buttonIcon: string | null;
  footnote: string | null;
}

const PLATO_EMPTY_STATES: Record<Exclude<JobApplicationTabEmptyStateStatus, "noResume">, PlatoEmptyStateConfig> = {
  ready: {
    icon: "plato",
    title: "Ask Plato to review this candidate",
    message: "Plato will analyze and score the candidate — role fit, relevant experience, skills, and gaps.",
    buttonLabel: "Generate review",
    buttonIcon: null,
    footnote: "Uses 1 credit · {creditsRemaining} remaining",
  },
  bulkQueued: {
    icon: "plato",
    title: "Queued for bulk review",
    message: "Plato is reviewing queued candidates one at a time. You can jump ahead and review the candidate now.",
    buttonLabel: "Generate review now",
    buttonIcon: null,
    footnote: "Uses 1 credit · {creditsRemaining} remaining",
  },
  processing: {
    icon: "plato",
    title: "Processing the resume",
    message: "Plato will automatically analyze and score the candidate once it's ready — no action needed.",
    buttonLabel: null,
    buttonIcon: null,
    footnote: null,
  },
  failed: {
    icon: "plato",
    title: "Plato couldn't analyze this candidate",
    message: "Something went wrong while analyzing this candidate. No credit was used. You can try again.",
    buttonLabel: "Try again",
    buttonIcon: "refresh-cw",
    footnote: "Uses 1 credit · {creditsRemaining} remaining",
  },
  noCredits: {
    icon: "plato",
    title: "You're out of Plato credits",
    message: "",
    buttonLabel: "Buy credits",
    buttonIcon: null,
    footnote: null,
  },
};

const NO_CREDITS_MESSAGE_ADMIN = "Purchase more credits to continue using Plato to score and analyze applicants.";
const NO_CREDITS_MESSAGE_MEMBER = "Ask an admin to purchase more credits to continue using Plato to score and analyze applicants.";

function resolveStatus(
  status: Exclude<JobApplicationTabEmptyStateStatus, "noResume">,
  options: { creditsRemaining?: number; isAdmin?: boolean; onClick?: () => void; failedDueToNoJobCriteria?: boolean; jobId?: number },
) {
  if (status === "failed" && options.failedDueToNoJobCriteria) {
    const credits = options.creditsRemaining !== undefined ? options.creditsRemaining : "";
    return {
      icon: "plato",
      title: "Plato needs clearer job requirements",
      message: (
        <>
          No requirements were found in your job description, so there&rsquo;s nothing to score against.
          Go to{" "}
          <Link to={`/jobs/${options.jobId}/setup/description`}>Job setup</Link> and add specific requirements, then try again.
        </>
      ),
      buttonLabel: "Try again",
      buttonIcon: "refresh-cw",
      onClick: options.onClick,
      footnote: `Uses 1 credit · ${credits} remaining`,
    };
  }

  const config = PLATO_EMPTY_STATES[status];

  let message: React.ReactNode = config.message;
  if (status === "noCredits") {
    message = options.isAdmin ? NO_CREDITS_MESSAGE_ADMIN : NO_CREDITS_MESSAGE_MEMBER;
  }

  let footnote: React.ReactNode = null;
  if (config.footnote) {
    const credits = options.creditsRemaining !== undefined ? options.creditsRemaining : "";
    footnote = config.footnote.replace("{creditsRemaining}", `${credits}`);
  }

  const result: any = {
    icon: config.icon,
    title: config.title,
    message,
    buttonLabel: config.buttonLabel || undefined,
    buttonIcon: config.buttonIcon || undefined,
    onClick: options.onClick,
    footnote,
  };

  if (status === "noCredits") {
    if (options.isAdmin) {
      result.buttonLink = "/hire/settings/plato-ai/billing";
    } else {
      result.buttonDisabled = true;
    }
  }

  return result;
}

function PlatoTabEmptyState(props: PlatoTabEmptyStateProps) {
  if (props.status === "noResume") {
    return (
      <JobApplicationTabEmptyState
        icon="plato"
        title="Plato needs a resume"
        message="Upload a resume on the resume tab, then come back to generate a review."
        buttonLabel="Go to resume tab"
        onClick={props.onClick}
        roomy
      />
    );
  }

  const resolved = resolveStatus(props.status, props);
  return <JobApplicationTabEmptyState {...resolved} roomy />;
}

export default PlatoTabEmptyState;
